import React, { useState, useEffect, useRef } from 'react';

// ChapterCard — cinematic chapter-close moment for non-competitive spaces.
//
// Chapters already exist (round_analysis actions carry chapter_title +
// host_intro) but they close silently and the title goes straight to the
// archive. This plays the moment LIVE: when a new round_analysis lands while
// you're watching, the space announces its own episode — dark vignette,
// "CHAPTER N", the title, the host's recap line — then gets out of the way.
//
// History never replays: played chapter ids persist per-viewer in
// localStorage (WYWA pattern), and everything present at first mount is
// marked played immediately.

const storageKey = (gameId) => `chaptercard_v1_${gameId}`;

const loadPlayed = (gameId) => {
  try {
    const raw = localStorage.getItem(storageKey(gameId));
    const arr = raw ? JSON.parse(raw) : null;
    if (Array.isArray(arr)) return new Set(arr);
  } catch (e) { /* private mode / corrupt state */ }
  return null;
};

const savePlayed = (gameId, set) => {
  try { localStorage.setItem(storageKey(gameId), JSON.stringify([...set].slice(-200))); } catch (e) { /* private mode */ }
};

const CHAPTER_CSS = `
@keyframes chapterVignetteIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes chapterTitleIn { 0% { opacity: 0; letter-spacing: 14px; transform: translateY(10px); } 100% { opacity: 1; letter-spacing: 4px; transform: translateY(0); } }
@keyframes chapterLineIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
@keyframes chapterRuleGrow { from { width: 0; opacity: 0; } to { width: 120px; opacity: 1; } }
`;

const AUTO_DISMISS_MS = 8200;

export default function ChapterCard({ gameId, actions = [], theme = {}, enabled = true }) {
  const accent = theme.accent || '#e91e63';
  const [card, setCard] = useState(null); // {num, title, intro}
  const playedRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled || !gameId) return;

    const analyses = actions
      .filter(a => a.type === 'round_analysis' && a.data)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    if (analyses.length === 0) return;

    // First sighting of this space: everything already written is history, not news.
    if (playedRef.current === null) {
      const stored = loadPlayed(gameId);
      if (stored) {
        playedRef.current = stored;
      } else {
        playedRef.current = new Set(analyses.map(a => a.id));
        savePlayed(gameId, playedRef.current);
        return;
      }
    }

    const fresh = analyses.filter(a => !playedRef.current.has(a.id));
    if (fresh.length === 0) return;

    // Mark ALL fresh as played, show only the latest (catch-up bursts play once)
    fresh.forEach(a => playedRef.current.add(a.id));
    savePlayed(gameId, playedRef.current);

    const latest = fresh[fresh.length - 1];
    const num = analyses.findIndex(a => a.id === latest.id) + 1;
    setCard({
      num,
      title: latest.data.chapter_title || null,
      intro: latest.data.host_intro || null,
    });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCard(null), AUTO_DISMISS_MS);
  }, [actions, enabled, gameId]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  if (!enabled || !card) return null;

  return (
    <div
      onClick={() => setCard(null)}
      style={{
        // z ABOVE the mobile dock (5000) + nav (5100): this is a tap-anywhere-to-
        // dismiss cinematic, but at 1450 the bottom ~140px of "anywhere" was the
        // live nav/composer, so a tap there switched tabs mid-cinematic instead.
        position: 'fixed', inset: 0, zIndex: 5300, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(ellipse at center, rgba(8,6,14,0.82) 0%, rgba(4,3,8,0.94) 100%)',
        animation: 'chapterVignetteIn 0.6s ease',
      }}
    >
      <style>{CHAPTER_CSS}</style>
      <div style={{ textAlign: 'center', padding: 24, maxWidth: 560 }}>
        <div style={{
          fontSize: '0.8em', fontWeight: 800, textTransform: 'uppercase',
          color: accent, letterSpacing: 4,
          animation: 'chapterTitleIn 0.9s ease both',
        }}>
          📖 Chapter {card.num}
        </div>
        <div style={{
          height: 2, background: accent, margin: '14px auto',
          animation: 'chapterRuleGrow 0.9s 0.25s ease both',
        }} />
        {card.title && (
          <div style={{
            fontSize: '1.7em', fontWeight: 800, color: '#fff', lineHeight: 1.25,
            fontStyle: 'italic', animation: 'chapterLineIn 0.7s 0.45s ease both',
          }}>
            {card.title}
          </div>
        )}
        {card.intro && (
          <div style={{
            fontSize: '0.95em', color: 'rgba(255,255,255,0.75)', marginTop: 14, lineHeight: 1.55,
            animation: 'chapterLineIn 0.7s 0.8s ease both',
          }}>
            {card.intro}
          </div>
        )}
        <div style={{
          fontSize: '0.68em', color: 'rgba(255,255,255,0.4)', marginTop: 22,
          animation: 'chapterLineIn 0.7s 1.4s ease both',
        }}>
          tap to keep watching
        </div>
      </div>
    </div>
  );
}
